<template>
  <UIModal
    v-model="isModalOpen"
    :front-blur="isRentOutBuildingModalOpen || announceModalOpen || isRepairBuildingModalOpen"
    height-fix
    @closed="emit('reset')"
  >
    <template #header>
      <div class="tw:flex tw:flex-col tw:text-center">
        <h2 class="tw:font-bold tw:text-white tw:max-sm:text-3xl tw:text-3xl">{{ $t('realtyModalTitle') }}</h2>
        <p class="tw:max-sm:text-base tw:text-xl">{{ $t('realtyModalSubtitle') }}</p>
      </div>
    </template>
    <template #default>
      <div class="tw:flex tw:flex-col tw:gap-4 tw:md:px-5 tw:px-5 tw:pt-5 tw:pb-5 tw:md:min-w-4xl tw:h-full wrapper">
        <div
          :class="{
            'tw:sm:grid tw:grid-cols-2': isUserAuthenticated,
            'tw:grid-cols-1': !isUserAuthenticated
          }"
          class="tw:flex tw:flex-col tw:gap-2 tw:sm:gap-4"
        >
          <UserDataCard
            v-if="isUserAuthenticated"
            :label="$t('realtyRewardPerYear')"
            :value="formattedUserEarnPerYear"
            class="tw:flex-1"
            icon-path="/img/tokens/doubloon.png"
          />
          <UserDataCard
            v-if="isUserAuthenticated"
            :label="$t('realtyMyEarnings')"
            :value="formattedUserClaimableEarnings"
            class="tw:flex-1"
            icon-path="/img/tokens/doubloon.png"
          />
        </div>
        <div class="tw:flex tw:flex-col tw:flex-1 tw:border tw:border-[#1E353E] tw:rounded-2xl tw:overflow-hidden">
          <UITabs :model-value="selectedTab" :tabs="tabs" class="tw:w-full" @update:model-value="handleTabChange" />
          <template v-if="selectedTab === RentTab.AvailableHouses">
            <div
              class="tw:flex tw:flex-col tw:flex-1 tw:justify-center tw:items-center tw:overflow-y-auto tab-container"
            >
              <div class="tab-container__cards-container">
                <div class="tab-container__header">
                  <div class="tab-container__claim-badge">
                    <div>
                      <img src="/img/tokens/doubloon.png" />
                      <span>{{ formattedUserClaimableEarnings }}</span>
                    </div>
                    <button
                      :disabled="isClaiming || userClaimableEarnings === '0'"
                      class="btn-primary tab-container__claim-btn"
                      @click="handleClaimReward"
                    >
                      {{ t('referralClaimButtonLabel') }}
                    </button>
                  </div>
                  <div class="tab-container__btns-container">
                    <HalloweenGenericButton
                      :disabled="
                        !rentEventStore.selectedHouses.length ||
                        rentOutLoading ||
                        selectedHouses.every((h) => h.durability >= 100) ||
                        displayedHouses.length <= 0
                      "
                      class="tab-container__btn"
                      @click="handleRepairBuildings"
                    >
                      {{ $t('realtyRepairButton') }} ({{
                        rentEventStore.selectedHouses.filter((h) => h.durability < 100).length
                      }})
                    </HalloweenGenericButton>
                    <HalloweenGenericButton
                      :disabled="
                        !rentEventStore.selectedHouses.length ||
                        rentOutLoading ||
                        rentEventStore.selectedHouses.every((h) => h.durability < 100) ||
                        displayedHouses.length <= 0
                      "
                      class="tab-container__btn"
                      @click="handleRentOutBuildings"
                    >
                      {{ $t('realtyRentOut') }} ({{
                        rentEventStore.selectedHouses.filter((h) => h.durability >= 100).length
                      }})
                    </HalloweenGenericButton>
                    <HalloweenGenericButton
                      :disabled="rentOutLoading || displayedHouses.length <= 0"
                      class="tab-container__btn"
                      @click="handleCheckAllClick"
                    >
                      {{
                        $t(
                          isAllSelected && displayedHouses.length > 0
                            ? 'realtyUncheckAllButton'
                            : 'realtyCheckAllButton'
                        )
                      }}
                    </HalloweenGenericButton>
                  </div>
                </div>

                <div class="tab-container__dropdowns-container">
                  <UIDropdown
                    v-model="housesListFilters.type"
                    :items="types.map((i) => ({ ...i, label: $t('rentFilter_type_' + i.label) }))"
                    :placeholder="$t('rentFilter_type_placeholder')"
                    multiple
                  />
                  <UIDropdown
                    v-model="housesListFilters.condition"
                    :items="conditions"
                    :placeholder="$t('rentFilter_condition_placeholder')"
                    multiple
                  />
                </div>

                <RentHouseCard
                  v-for="house in displayedHouses"
                  :key="house.tokenId"
                  :house-data="house"
                  :root-loading="rentOutLoading"
                  :selected="rentEventStore.selectedHouses.some((item) => getHouseKey(item) === getHouseKey(house))"
                  mode="available"
                  @repair="handleRepairExactBuilding"
                  @rent-out="handleRentOutExactBuilding"
                  @update:selected="rentEventStore.switchHouseSelection(house)"
                />

                <div v-if="displayedHouses.length < 1" class="tw:flex tw:flex-col tw:items-center tw:gap-6 tw:mt-2">
                  <div class="tw:flex tw:flex-col tw:items-center">
                    <h3 class="tw:text-white tw:text-2xl">{{ $t('realtyEmptyStateNoHouses') }}</h3>
                    <p class="tw:max-sm:hidden tw:text-xl tw:text-center">{{ $t('realtyEmptyStateBuildFirst') }}</p>
                  </div>
                  <button class="btn-primary" @click="handleBuild">{{ $t('realtyButtonBuild') }}</button>
                </div>
              </div>
            </div>
          </template>
          <template v-if="selectedTab === RentTab.StakedHouses">
            <div
              :class="{ 'tab-container': displayedHouses.length >= 1 }"
              class="tw:flex tw:flex-col tw:flex-1 tw:justify-center tw:items-center tw:overflow-y-auto"
            >
              <div class="tab-container__cards-container">
                <div class="tab-container__header">
                  <div class="tab-container__claim-badge">
                    <div>
                      <img alt="" src="/img/tokens/doubloon.png" />
                      <span>{{ formattedUserClaimableEarnings }}</span>
                    </div>
                    <button
                      :disabled="isClaiming || userClaimableEarnings === '0'"
                      class="btn-primary tab-container__claim-btn"
                      @click="handleClaimReward"
                    >
                      {{ t('referralClaimButtonLabel') }}
                    </button>
                  </div>
                  <div v-if="displayedHouses.length >= 1" class="tab-container__btns-container">
                    <HalloweenGenericButton
                      :disabled="
                        !rentEventStore.selectedHouses.length ||
                        selectedHouses.every((h) => h.durability >= 100) ||
                        displayedHouses.length <= 0
                      "
                      class="tab-container__btn"
                      @click="handleRepairBuildings"
                    >
                      {{ $t('realtyRepairButton') }} ({{
                        rentEventStore.selectedHouses.filter((h) => h.durability < 100).length
                      }})
                    </HalloweenGenericButton>
                    <HalloweenGenericButton
                      :disabled="!rentEventStore.selectedHouses.length || displayedHouses.length <= 0"
                      class="tab-container__btn"
                      @click="handleTakeBack"
                    >
                      {{ $t('realtyTackBack') }} ({{ rentEventStore.selectedHouses.length }})
                    </HalloweenGenericButton>
                    <HalloweenGenericButton
                      :disabled="displayedHouses.length <= 0"
                      class="tab-container__btn"
                      @click="handleCheckAllClick"
                    >
                      {{
                        $t(
                          isAllSelected && displayedHouses.length > 0
                            ? 'realtyUncheckAllButton'
                            : 'realtyCheckAllButton'
                        )
                      }}
                    </HalloweenGenericButton>
                  </div>
                </div>

                <div class="tab-container__dropdowns-container">
                  <UIDropdown
                    v-model="housesListFilters.type"
                    :items="types.map((i) => ({ ...i, label: $t('rentFilter_type_' + i.label) }))"
                    :placeholder="$t('rentFilter_type_placeholder')"
                    multiple
                  />
                  <UIDropdown
                    v-model="housesListFilters.condition"
                    :items="conditions"
                    :placeholder="$t('rentFilter_condition_placeholder')"
                    multiple
                  />
                </div>

                <RentHouseCard
                  v-for="house in displayedHouses"
                  :key="house.tokenId"
                  :house-data="house"
                  :root-loading="rentOutLoading"
                  :selected="rentEventStore.selectedHouses.some((item) => getHouseKey(item) === getHouseKey(house))"
                  mode="rented"
                  @repair="handleRepairExactBuilding"
                  @rent-out="handleRentOutExactBuilding"
                  @update:selected="rentEventStore.switchHouseSelection(house)"
                  @tack-back="handleTakeBackExactBuilding"
                />

                <div v-if="displayedHouses.length < 1" class="tw:flex tw:flex-col tw:items-center tw:gap-6 tw:mt-2">
                  <div class="tw:flex tw:flex-col tw:items-center">
                    <h3 class="tw:text-white tw:text-2xl">{{ $t('realtyEmptyStateNoHouses') }}</h3>
                    <p class="tw:max-sm:hidden tw:text-xl tw:text-center">{{ $t('realtyEmptyStateBuildFirst') }}</p>
                  </div>
                  <button class="btn-primary" @click="handleBuild">{{ $t('realtyButtonBuild') }}</button>
                </div>
              </div>
            </div>
          </template>
          <template v-if="selectedTab === RentTab.Market">
            <div
              :class="{ 'tab-container': displayedHouses.length >= 1 }"
              class="tw:flex tw:flex-col tw:flex-1 tw:justify-center tw:items-center tw:overflow-y-auto tab-container"
            >
              <div class="tab-container__cards-container tab-container__cards-container--market">
                <RentHouseCard
                  v-for="house in marketHouses"
                  :key="house.nftAddress"
                  :house-data="house"
                  mode="estate-market"
                  @build="handleBuild"
                />
              </div>
              <div v-if="marketHouses.length < 1" class="tw:flex tw:flex-col tw:items-center tw:gap-6">
                <div class="tw:flex tw:flex-col tw:items-center">
                  <h3 class="tw:text-white tw:text-2xl">{{ $t('realtyEmptyStateNoHouses') }}</h3>
                  <p class="tw:text-xl">{{ $t('realtyEmptyStateBuildFirst') }}</p>
                </div>
                <button class="btn-primary" @click="handleBuild">{{ $t('realtyButtonBuild') }}</button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <RepairBuildingModal
        v-model="isRepairBuildingModalOpen"
        :resources="houseFixResources"
        :season-points="houseFixSeasonPoints"
        :selected-houses="housesToRepair"
      />
      <StopRentingWarningModal v-model="isRentOutBuildingModalOpen" :selected-houses="housesToTakeBackOut" />
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import HalloweenGenericButton from '~/components/Halloween/HalloweenGenericButton.vue';
import RentHouseCard from '~/components/Rent/RentHouseCard.vue';
import { UserDataCard } from '~/entities/realty';
import { RepairBuildingModal, StopRentingWarningModal } from '~/features/realty';
import {
  type IHouseFixResource,
  type IUserHouseInterface,
  RentTab,
  useRentEventStore
} from '~/stores/rent-event-store';
import { FilterCondition, FilterType } from '~/stores/rent-event-store/types';
import type { IUserModel } from '~/types/apiService';
import { Formatters } from '~/utils/formatters/formatters';

const { t } = useI18n();
const pendingTab = ref<RentTab | null>(null);
const isModalOpen = defineModel<boolean>({ default: false });
const { login } = useAuth();
const { data: userData } = useNuxtData<IUserModel>('userData');
const isUserAuthenticated = computed(() => !!userData.value);
const rentEventStore = useRentEventStore();
const runtimeConfig = useRuntimeConfig();
const bus = useGoToBuildingBus();
const { isMobile } = useDevice();
const {
  selectedTab,
  userClaimableEarnings,
  marketHouses,
  displayedHouses,
  housesListFilters,
  announceModalOpen,
  estimatedRewardPerYear
} = storeToRefs(rentEventStore);

const emit = defineEmits(['reset', 'resume', 'pause']);

watch(
  () => [rentEventStore.isManorModalOpen],
  (value) => {
    if (value.some((v) => v)) {
      emit('pause');
    } else {
      emit('resume');
    }
  }
);

const goToBuildingByHash = inject<((hash: string) => void) | null>('goToBuildingByHash', null);

const houseFixResources = ref<IHouseFixResource[]>([]);
const houseFixSeasonPoints = ref<string>('0');

const campTestHash = '0x81653e3ff92381ab7017764ae2f363b2b1604833c8f28d469066775283ca4554';
const campProdHash = '0xb12bf3b2dd0a0b6a962bdcc63b163adb2f246c3a3a9bb4712dcf6453e5ccd191';

const formattedUserClaimableEarnings = computed(() =>
  Formatters.number.renderString((f) => f.formatNumber(userClaimableEarnings.value))
);

const formattedUserEarnPerYear = computed(() =>
  Formatters.number.renderString((f) => f.formatNumber(estimatedRewardPerYear.value))
);

const tabs = computed(() => [
  { label: t('realtyTabAvailableHouses'), value: RentTab.AvailableHouses },
  { label: t('realtyTabRentedHouses'), value: RentTab.StakedHouses },
  { label: t('realtyTabMarket'), value: RentTab.Market }
]);

const isAllSelected = computed(() => rentEventStore.selectedHouses.length === rentEventStore.displayedHouses.length);
const isClaiming = ref(false);

const handleCheckAllClick = () => {
  if (isAllSelected.value) {
    rentEventStore.selectedHouses = [];
  } else {
    rentEventStore.selectedHouses = rentEventStore.displayedHouses;
  }
};
const handleClaimReward = () => {
  isClaiming.value = true;
  rentEventStore.claimUserRewards().finally(() => (isClaiming.value = false));
};

const handleBuild = () => {
  const hash = runtimeConfig.public.env === 'prod' ? campProdHash : campTestHash;
  if (isMobile) {
    bus.emit(hash);
  }

  isModalOpen.value = false;
  goToBuildingByHash?.(hash);
};

const { getHouseKey } = rentEventStore;

// dropdowns data
const types = Object.values(FilterType).map((label) => ({ id: label, label }));
const conditions = Object.values(FilterCondition).map((label) => ({ id: label, label }));

// const handleTackBack = (actionName: string) => console.log(`Действие совершено: ${actionName}`);
// const handleBuild = (actionName: string) => console.log(`Действие совершено: ${actionName}`);

const handleTabChange = async (val: string) => {
  const newTab = val as RentTab;

  if (isUserAuthenticated.value || newTab === RentTab.Market) {
    rentEventStore.selectedTab = newTab;
    pendingTab.value = null;
    return;
  }

  pendingTab.value = newTab;

  await login();
};

watch(isUserAuthenticated, (isAuthenticated) => {
  if (isAuthenticated && pendingTab.value) {
    rentEventStore.selectedTab = pendingTab.value;
    pendingTab.value = null;
  }
});

watch(isModalOpen, (isOpen) => {
  if (!isOpen) {
    pendingTab.value = null;
  } else if (!isUserAuthenticated.value) {
    rentEventStore.selectedTab = RentTab.Market;
  }
});

const isRepairBuildingModalOpen = ref(false);
const housesToRepair = ref<IUserHouseInterface[]>([]);
const { selectedHouses } = storeToRefs(rentEventStore);
const rentOutLoading = ref(false);

function handleRepairExactBuilding(house: IUserHouseInterface) {
  housesToRepair.value = [house];

  rentEventStore.getHouseFixResources([house]).then((res) => {
    rentEventStore.getHouseFixSeasonPoints(res).then((sp) => {
      houseFixSeasonPoints.value = sp;
    });

    houseFixResources.value = res;
    isRepairBuildingModalOpen.value = true;
  });
}

function handleRepairBuildings() {
  if (selectedHouses.value.length === 0) return;
  housesToRepair.value = selectedHouses.value;
  rentEventStore.getHouseFixResources(selectedHouses.value).then((res) => {
    rentEventStore.getHouseFixSeasonPoints(res).then((sp) => {
      houseFixSeasonPoints.value = sp;
    });

    houseFixResources.value = res;
    isRepairBuildingModalOpen.value = true;
  });
}

const isRentOutBuildingModalOpen = ref(false);
const housesToRentOut = ref<IUserHouseInterface[]>([]);
const housesToTakeBackOut = ref<IUserHouseInterface[]>([]);

function handleRentOutExactBuilding(house: IUserHouseInterface) {
  housesToRentOut.value = [house];
  //isRentOutBuildingModalOpen.value = true;

  rentOutLoading.value = true;
  rentEventStore.stakeHouses(housesToRentOut.value).finally(() => (rentOutLoading.value = false));
}

function handleRentOutBuildings() {
  if (selectedHouses.value.length === 0) return;
  housesToRentOut.value = selectedHouses.value;
  //isRentOutBuildingModalOpen.value = true;

  rentOutLoading.value = true;
  rentEventStore.stakeHouses(housesToRentOut.value).finally(() => (rentOutLoading.value = false));
}

function handleTakeBack() {
  if (selectedHouses.value.length === 0) return;
  housesToTakeBackOut.value = selectedHouses.value;
  isRentOutBuildingModalOpen.value = true;

  //rentEventStore.stakeHouses(housesToTakeBackOut.value);
}

function handleTakeBackExactBuilding(house: IUserHouseInterface) {
  housesToTakeBackOut.value = [house];

  isRentOutBuildingModalOpen.value = true;

  //rentEventStore.stakeHouses(housesToTakeBackOut.value);
}
</script>

<style lang="scss" scope>
.wrapper {
  @media screen and (max-width: 768px) {
    padding: 10px;
  }
}
.tab-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  height: calc(100% - 56px);

  @media screen and (max-width: 768px) {
    gap: 10px;
    padding: 0;
  }

  &__header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 10px;
    padding-bottom: 0;
    gap: 10px;

    @media screen and (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
  }

  &__claim-badge {
    height: 56px;
    background: linear-gradient(180deg, #071821 0%, #040e13 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    //max-width: 320px;
    width: 100%;

    div {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    img {
      width: 24px;
    }

    span {
      font-family: Grenze;
      font-weight: 500;
      font-size: 20px;
      line-height: 1;
      color: #fff;
    }
  }

  &__btn {
    white-space: nowrap;

    @media screen and (max-width: 768px) {
      padding: 4px !important;
      width: fit-content !important;
    }

    @media screen and (max-width: 520px) {
      white-space: initial;
    }
  }

  &__claim-btn {
    @media screen and (max-width: 768px) {
      width: fit-content;
    }
  }

  &__btns-container {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    justify-content: flex-end;

    @media screen and (max-width: 768px) {
      justify-content: center;
    }
  }

  &__dropdowns-container {
    display: flex;
    align-items: center;
    gap: 20px;
    width: 100%;
    justify-content: flex-end;

    @media screen and (max-width: 768px) {
      gap: 4px;
      flex-direction: column;
      .base-dropdown {
        width: 100%;
        max-width: 100%;
      }
    }
    padding: 0 10px;
  }

  &__cards-container {
    overflow-y: auto;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 10px;

    &--market {
      height: 100%;
    }
  }
}
</style>
